<script setup>
import { useStore } from 'vuex'
import DataTableField from '@/components/form/DataTableField.vue'
import TableActionMenu from '@/components/common/TableActionMenu.vue'
import FilterChipGroup from '@/components/boundary-condition/FilterChipGroup.vue'
import DropDownSingleSelect from '@/components/form/DropDownSingleSelect.vue'
import { computed, onMounted, toRef, watch } from 'vue'
import { actionMenuMixin } from '@/mixins/actionMenuMixin'
import { dirtyFieldMixin } from '@/mixins/dirtyFieldMixin'
import { boundaryConditionTableMixin } from '@/mixins/boundaryConditionTableMixin'

const STORE_NAMESPACE = 'settlement'

const store = useStore()

const props = defineProps({
  items: { type: Array, required: true },
  years: { type: Array, required: true },
  count: { type: Number, require: true },
  editable: { require: false, default: true }
})

const items = toRef(props, 'items')

const { x, y, showMenu, selectedItem, show } = actionMenuMixin()
const { dirtyItems, addDirty, getDirtyClass } = dirtyFieldMixin()

const deleteFromState = (item) => {
  store.dispatch('settlement/deleteFromState', item)
}

const deleteItems = (input) => {
  store.dispatch('settlement/deleteByReferencePoint', input)
}

const emit = defineEmits(['changePage'])

const {
  itemPerPage,
  referencePointsOptions,
  referencePointsShow,
  activeYears,
  filterYearId,
  borderBottom,
  updateOptions,
  deleteReferencePoint
} = boundaryConditionTableMixin(
  STORE_NAMESPACE,
  items,
  selectedItem,
  getDirtyClass,
  deleteItems,
  deleteFromState,
  emit
)

const loading = computed(() => store.state.settlement.loading)

watch(
  () => props.years,
  () => {
    activeYears.value = JSON.parse(JSON.stringify(props.years))
  }
)

const setReferencePoints = (input) => {
  store.dispatch('settlement/setReferencePoint', input)
}

const setParameter = (input) => {
  store.dispatch('settlement/setParameter', input)
}

const createFilters = () => {
  activeYears.value = JSON.parse(JSON.stringify(props.years))
}

defineExpose({
  dirtyItems
})

onMounted(() => {
  createFilters()
})
</script>

<template>
  <div>
    <div style="justify-content: space-between; display: flex">
      <v-card variant="flat" v-if="years.length">
        <FilterChipGroup :items="years" :active-items="activeYears" parameter="year" />
      </v-card>
      <slot></slot>
    </div>
    <v-row>
      <v-col>
        <v-data-table-server
          :items-length="count ?? 0"
          :items="referencePointsShow"
          density="compact"
          hide-no-data
          :items-per-page="itemPerPage"
          @update:options="updateOptions"
          :footer-props="{
            disableItemsPerPage: true
          }"
        >
          <template #headers>
            <tr>
              <th style="min-width: 200px">
                <span class="pl-1">Reference point</span>
              </th>
              <th
                v-for="year in activeYears"
                class="year-header"
                :key="`settlement-${year.id}`"
                :style="filterYearId(year.id)"
              >
                <span class="pl-1">{{ year.year }}</span>
              </th>
            </tr>
          </template>
          <template #body>
            <tr v-if="loading" class="v-data-table-progress">
              <th colspan="999" style="text-align: center">
                <v-progress-linear indeterminate color="primary" />
              </th>
            </tr>
            <template v-else>
              <tr
                v-for="referencePoint in referencePointsShow"
                :key="referencePoint.id"
                @contextmenu="show($event, referencePoint)"
              >
                <td>
                  <DropDownSingleSelect
                    :options="referencePointsOptions"
                    :item="referencePoint.id"
                    @update="setReferencePoints"
                    :editable="editable"
                    @addDirty="addDirty(referencePoint, referencePoint.id)"
                    :class="borderBottom(referencePoint)"
                  />
                </td>
                <td
                  v-for="item in referencePoint.settlement_set"
                  :key="`settlement-${item.id}`"
                  :style="filterYearId(item.year)"
                >
                  <DataTableField
                    width="75px"
                    @update="addDirty(item, 'settlement')"
                    @set="setParameter"
                    :item="item"
                    :editable="editable"
                    :class="getDirtyClass(item, 'settlement')"
                    parameter="settlement"
                  />
                </td>
              </tr>
            </template>
          </template>
        </v-data-table-server>
      </v-col>
    </v-row>
    <TableActionMenu
      @update:show-menu="showMenu = $event"
      v-if="editable"
      :add-button="false"
      :selected-item="selectedItem"
      :show-menu="showMenu"
      :x="x"
      :y="y"
      @action="deleteReferencePoint()"
    />
  </div>
</template>
<style scoped>
.v-table:deep(th) {
  border-bottom: 1px solid rgba(255, 255, 255, 0) !important;
  text-align: left;
  padding: 0 !important;
  font-size: 0.75rem;
  font-family: Roboto, sans-serif;
  font-weight: bold !important;
  color: rgba(0, 0, 0, 0.6);
}

.v-table:deep(td) {
  padding: 0 !important;
}

.v-table:deep(th) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.year-header {
  min-width: 75px;
}
</style>
